// src/photos.js
//
// Product photos, and the site's own photos, wherever they come from.
//
// A product's photos are kept on its `products` document as `images`, a list
// in display order, cover first. Each entry is either a photo an admin
// uploaded — `{ id, width, height, placeholder, variants }`, written by the
// `productPhoto` function — or `{ builtIn: key }`, naming one of the photos
// that ship with the site (src/builtInPhotos.js). A document with no `images`
// at all shows the built-in photos filed under its photo key — the six
// original products keep the name of their old stock record as `photoKey`,
// and any other product is filed under its id — and an empty list shows none.
//
// Everything that shows a product photo goes through `productPhotos`, so the
// storefront, the cart and the dashboard can never disagree about which photo
// is the cover.

import { BUILT_IN_PHOTOS, BUILT_IN_PRODUCT_PHOTOS, BUILT_IN_SITE_PHOTOS } from './builtInPhotos';

/** The built-in photos filed under a photo key, as `images` entries. */
export const builtInEntries = (photoKey) =>
  (BUILT_IN_PRODUCT_PHOTOS[photoKey] || []).map((key) => ({ builtIn: key }));

/**
 * The name a product's photos are filed under, here and in Cloud Storage: its
 * `photoKey` if it has one, otherwise its id. functions/productPhotos.js
 * decides the same way.
 */
export const photoKeyOf = (product) =>
  (typeof product?.photoKey === 'string' && product.photoKey) ||
  (product?.id != null ? String(product.id) : undefined);

/** What identifies an entry, for comparing two lists of them. */
export const entryKey = (entry) => (entry?.builtIn ? `builtIn:${entry.builtIn}` : `upload:${entry?.id}`);

const validVariant = (variant) =>
  variant &&
  Number(variant.width) > 0 &&
  typeof variant.url === 'string' &&
  /^(https:\/\/|\/)/.test(variant.url);

/**
 * One `images` entry as something the page can draw: `{ key, width, height,
 * placeholder, variants }`, with the variants narrowest first. Null for an
 * entry that names a built-in photo that no longer exists, or that is missing
 * the parts an <img> needs.
 */
export function resolvePhoto(entry) {
  if (!entry) return null;
  const photo = entry.builtIn ? BUILT_IN_PHOTOS[entry.builtIn] : entry;
  if (!photo || !Array.isArray(photo.variants)) return null;
  const variants = photo.variants
    .filter(validVariant)
    .map((variant) => ({ ...variant, width: Number(variant.width) }))
    .sort((a, b) => a.width - b.width);
  if (variants.length === 0) return null;
  const widest = variants[variants.length - 1];
  return {
    key: entryKey(entry),
    width: Number(photo.width) || widest.width,
    height: Number(photo.height) || widest.height || widest.width,
    placeholder: typeof photo.placeholder === 'string' ? photo.placeholder : '',
    variants,
  };
}

/** The photos to show for a product, given its record (which may be missing). */
export function productPhotos(product) {
  const entries = Array.isArray(product?.images) ? product.images : builtInEntries(photoKeyOf(product));
  return entries.map(resolvePhoto).filter(Boolean);
}

/** `srcset` for an <img>: every width the photo is kept at. */
export const srcSetOf = (photo) =>
  photo.variants.map((variant) => `${variant.url} ${variant.width}w`).join(', ');

/**
 * The single URL to fall back on where `srcset` is not read, and to hand to
 * anything that wants one address: the narrowest width at least `atLeast`
 * pixels wide, or the widest there is.
 */
export const photoUrl = (photo, atLeast = 640) =>
  (photo.variants.find((variant) => variant.width >= atLeast) || photo.variants[photo.variants.length - 1]).url;

//
// ——— WHAT THE LAST STOCK READ SAID ————————————————————————————————————————
//
// The cart, its dropdown and checkout each read the products for prices, but
// only after they have drawn. Remembering the photos the last read carried —
// for the session, and across visits — lets them draw the right photo at once
// rather than an empty square that fills in, or worse, a built-in photo that
// is then swapped for the one the admin uploaded.

// Versioned: the entry written before the catalogue was dynamic named only the
// images, and cannot say where a product's built-in photos are filed.
const STORAGE_KEY = 'productPhotos.v2';
const OLD_STORAGE_KEY = 'productPhotos';
const EVENT = 'productPhotosUpdated';

const readStored = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

// { productId: { images: images-field-or-null, photoKey } }. A key that is
// present means "known"; null images means the product had no `images`, so
// the built-in photos filed under its photoKey apply.
let known = null;
const knownImages = () => {
  if (!known) {
    known = readStored();
    try {
      localStorage.removeItem(OLD_STORAGE_KEY);
    } catch (err) {
      // Nothing to tidy.
    }
  }
  return known;
};

const rememberedShape = (row) => ({
  images: Array.isArray(row.images) ? row.images : null,
  photoKey: typeof row.photoKey === 'string' ? row.photoKey : null,
});

/** Records the photos a product read returned. Called by every product read in db.js. */
export function rememberProductPhotos(rows) {
  const current = knownImages();
  let changed = false;
  (rows || []).forEach((row) => {
    if (!row || row.id == null) return;
    const id = String(row.id);
    const next = rememberedShape(row);
    if (!(id in current) || JSON.stringify(current[id]) !== JSON.stringify(next)) {
      current[id] = next;
      changed = true;
    }
  });
  if (!changed) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (err) {
    // A full or blocked store only costs the next visit its head start.
  }
  window.dispatchEvent(new Event(EVENT));
}

/**
 * The photos last seen for a product, or null when no product read has said
 * yet — in which case nothing should be drawn, rather than a guess.
 */
export function rememberedProductPhotos(productId) {
  const current = knownImages();
  const entry = current[String(productId)];
  if (!entry || typeof entry !== 'object') return null;
  return productPhotos({
    id: String(productId),
    images: entry.images ?? undefined,
    photoKey: entry.photoKey,
  });
}

/** Calls `listener` whenever a stock read brings different photos. */
export function onProductPhotosChange(listener) {
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

/** For tests: forget everything remembered. */
export function forgetProductPhotos() {
  known = {};
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(OLD_STORAGE_KEY);
  } catch (err) {
    // Nothing to forget.
  }
}

//
// ——— THE SITE'S OWN PHOTOS ———————————————————————————————————————————————
//
// Photos that belong to the site rather than a product — the background behind
// the home page heading, which plays as a slideshow, and the sign-in panel,
// which shows the first of them — kept on the `site/photos` document: a list
// of entries per slot, in the same shape as a product's `images`, plus how
// long each slide shows. A slot with nothing set shows its built-in photo.

export const SITE_PHOTO_SLOTS = ['hero'];

// Everything on the document that the pages need, and so is remembered.
const SITE_FIELDS = ['hero', 'heroInterval', 'productInterval'];

/** How long each background photo shows when nothing else has been chosen. */
export const DEFAULT_SLIDE_SECONDS = 6;
export const SLIDE_SECONDS_CHOICES = [4, 6, 8, 10, 15];

/** The built-in photos for a slot, as entries. */
export const builtInSiteEntries = (slot) =>
  BUILT_IN_SITE_PHOTOS[slot] ? [{ builtIn: BUILT_IN_SITE_PHOTOS[slot] }] : [];

/**
 * The entries an admin set for a slot, or null for none. A single entry rather
 * than a list is what the background was saved as before it became a
 * slideshow, and still reads as a list of one.
 */
export const siteEntries = (slot, data) => {
  const value = data?.[slot];
  if (Array.isArray(value)) return value.length ? value : null;
  return value ? [value] : null;
};

/** The photos to show in a slot, given the `site/photos` document (or none). */
export function sitePhotos(slot, data) {
  const own = (siteEntries(slot, data) || []).map(resolvePhoto).filter(Boolean);
  return own.length ? own : builtInSiteEntries(slot).map(resolvePhoto).filter(Boolean);
}

/** The first of them: what shows where there is room for only one. */
export const sitePhoto = (slot, data) => sitePhotos(slot, data)[0] || null;

/** Seconds each background photo shows for. */
export const slideSeconds = (data) => {
  const seconds = Number(data?.heroInterval);
  return seconds >= 3 && seconds <= 30 ? seconds : DEFAULT_SLIDE_SECONDS;
};

// How long each photo on a product card shows before the card moves on by
// itself, also set in the dashboard. 0 means the cards do not move on their
// own at all; they can still be paged through.
export const DEFAULT_PRODUCT_SLIDE_SECONDS = 5;
export const PRODUCT_SLIDE_SECONDS_CHOICES = [0, 4, 5, 6, 8, 10, 15];

/** Seconds each product card photo shows for, or 0 for not moving on its own. */
export const productSlideSeconds = (data) => {
  if (data?.productInterval === 0) return 0;
  const seconds = Number(data?.productInterval);
  return seconds >= 3 && seconds <= 30 ? seconds : DEFAULT_PRODUCT_SLIDE_SECONDS;
};

// Remembered like the products' photos, so a returning visitor's background
// is drawn at once rather than after the document has been read. Unlike them,
// "never read" and "read, nothing set" differ: the first draws no photo yet,
// the second the built-in one.
const SITE_KEY = 'sitePhotos';
const SITE_EVENT = 'sitePhotosUpdated';
let siteKnown; // undefined: storage not looked at yet; null: never read

const siteFields = (data) =>
  Object.fromEntries(SITE_FIELDS.filter((field) => data?.[field] != null).map((field) => [field, data[field]]));

/** What the last read of `site/photos` returned, or null if there has been none. */
export function rememberedSitePhotos() {
  if (siteKnown === undefined) {
    try {
      const raw = localStorage.getItem(SITE_KEY);
      siteKnown = raw ? JSON.parse(raw) : null;
    } catch (err) {
      siteKnown = null;
    }
  }
  return siteKnown;
}

/** Records what a read (or the dashboard's save) of `site/photos` said. */
export function rememberSitePhotos(data) {
  const next = siteFields(data);
  const current = rememberedSitePhotos();
  if (current && JSON.stringify(current) === JSON.stringify(next)) return;
  siteKnown = next;
  try {
    localStorage.setItem(SITE_KEY, JSON.stringify(next));
  } catch (err) {
    // Only costs the next visit its head start.
  }
  window.dispatchEvent(new Event(SITE_EVENT));
}

/** Calls `listener` whenever the site's photos are found to have changed. */
export function onSitePhotosChange(listener) {
  window.addEventListener(SITE_EVENT, listener);
  return () => window.removeEventListener(SITE_EVENT, listener);
}

/** For tests: forget what was remembered. */
export function forgetSitePhotos() {
  siteKnown = undefined;
  try {
    localStorage.removeItem(SITE_KEY);
  } catch (err) {
    // Nothing to forget.
  }
}
